import { redirectToBridgeLogin, signOutFromBridge } from './bridge-auth'
import { isUnauthorizedError } from './supabase'

/**
 * Reparo de sessão em 401.
 *
 * Cenário real: o cookie do bridge continua válido, mas o `supabaseAccessToken`
 * que ele carrega já expirou. A sessão parece boa e toda query devolve 401. Sem
 * tratamento, o usuário vê um app logado onde nada carrega.
 *
 * A saída é derrubar a sessão do bridge e refazer o login. Como isso é uma
 * navegação, precisa de trava: se o relogin não resolver — membership revogada,
 * por exemplo — a repetição vira um loop de redirecionamento infinito. A flag
 * fica em `sessionStorage` para valer só nesta aba e nesta sessão de navegação.
 */
const REPAIR_FLAG = 'sistemas:bridge-repair-attempted'

function alreadyAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(REPAIR_FLAG) === '1'
  } catch {
    // Sem sessionStorage não há como travar o loop; melhor não tentar reparar.
    return true
  }
}

function markAttempted(): void {
  try {
    window.sessionStorage.setItem(REPAIR_FLAG, '1')
  } catch {
    /* storage indisponível */
  }
}

/** Chamar após um login bem-sucedido, para liberar um reparo futuro. */
export function clearRepairAttempt(): void {
  try {
    window.sessionStorage.removeItem(REPAIR_FLAG)
  } catch {
    /* storage indisponível */
  }
}

/**
 * Decide se vale reparar. Devolve `true` quando assumiu o controle e vai
 * navegar — nesse caso o chamador deve parar de renderizar e mostrar um estado
 * de carregamento, porque a página está de saída.
 */
export function shouldRepairSession(error: unknown): boolean {
  if (!isUnauthorizedError(error)) return false
  if (alreadyAttempted()) return false

  markAttempted()
  void signOutFromBridge().finally(() => {
    redirectToBridgeLogin(window.location.href)
  })
  return true
}
